import React, { forwardRef, useState, useEffect } from 'react';
import { Switch } from 'react-native';
import styled from 'styled-components';
import { Controller } from 'react-hook-form';
import { Text, Hint } from '../styles';
import { elRed } from 'src/styles/colors';
import { abbreviations, statesList } from './states';
import moment from 'moment';
import Select from "react-select";
import colors from 'src/styles/colors';

export const StyledTextInput = styled.input`
  border-width: 1px;
  border-color: black;
  padding: 6px;
  background-color: #fff;
  height: 17px;
`;

export const StyledPicker = styled.select`
  border-color: black;
  padding: 3px;
  padding-top: 6px;
  padding-bottom: 6px;
  font-size: 14px;
`;

export const selectStyles = {
  control: provided => ({
    ...provided,
    borderColor: 'black',
    minHeight: '31px',
    height: '31px',
    fontSize: '14px',
    borderRadius: 0,
    marginTop: '-6px',
    marginLeft: '5px',
    overflowX: 'auto',
    overflowY: 'auto'
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: provided => ({ ...provided, zIndex: 9999 })
};

const searchablePickerStyles = {
  ...selectStyles,
  control: (provided, state) => ({
    ...selectStyles.control(provided, state),
    overflowX: 'hidden',
    overflowY: 'hidden',
    cursor: 'pointer'
  }),
  singleValue: provided => ({ ...provided, color: '#000' }),
  input: provided => ({ ...provided, color: '#000' }),
  placeholder: provided => ({ ...provided, color: '#808080' }),
  option: (provided, state) => ({
    ...provided,
    color: '#000',
    cursor: 'pointer',
    backgroundColor: state.isFocused ? '#f0f0f0' : '#fff'
  }),
  indicatorsContainer: provided => ({
    ...provided,
    height: '31px',
    alignItems: 'center'
  }),
  dropdownIndicator: provided => ({ ...provided, color: '#808080', padding: '0 4px' })
};

const ErrorText = styled.div`
  padding-left: 10px;
  color: red;
`;

export const Field = styled.div`
  margin-bottom: 10px;
`;

export const SwitchField = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 5px;
  margin-bottom: 10px;
  align-items: center;
`;

const SwitchLabel = styled.span`
  margin-left: 10px;
`;

const labelStyle = {
  fontSize: '12px',
}

export const FieldLabel = ({ name, label, hint, errors }) => {
  let errorMessage;

  if (errors?.[name]) {
    errorMessage = errors[name].message || 'Required';
  }

  return (
    <>
      <div className="field-label"
        style={{flexDirection: 'row', marginBottom: '5px', marginLeft: '5px'}}
      >
        <Text style={{color: elRed, fontWeight: 600, fontSize: '14px'}}>{label}</Text>
        {errorMessage && (
          <ErrorText className="field-error">{errorMessage}.</ErrorText>
        )}
      </div>
      {hint && <Hint>{hint}</Hint>}
    </>
  );
};

export const TextInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    placeholder,
    type,
    style,
    ...controllerProps
  } = props;

  return (
    <Field className="form-field" style={style}>
      <div className="form-field-inner" style={{display: 'flex', flexDirection: 'column'}}>
        <FieldLabel name={props.name} label={label} hint={hint} errors={errors} style={labelStyle} />
        <Controller
          {...controllerProps}
          control={control}
          render={({ field }) => (
            <StyledTextInput
              {...field}
              ref={ref}
              type={type || 'text'}
              placeholder={placeholder}
              style={{width: '100%', height: '31px', boxSizing: 'border-box', fontSize: '14px', marginLeft: '5px', marginTop: '-6px'}}
            />
          )}
        />
      </div>
    </Field>
  );
});

export const TextAreaInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    placeholder,
    secureTextEntry,
    rows,
    width,
    ...controllerProps
  } = props;

  return (
    <Field className="form-field">
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <textarea
            {...field}
            rows={rows || 4}
            style={{width: width, fontFamily: 'sans-serif'}}
          />
        )}
      />
    </Field>
  );
});

export const CheckboxInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    checked,
    ...controllerProps
  } = props;

  return (
    <Field className="form-field">
      <div style={{display: 'flex', flexDirection: 'row'}}>
        <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
        <Controller
          {...controllerProps}
          control={control}
          render={({ field }) => (
            <input
              type='checkbox'
              checked={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>
    </Field>
  );
});

export const PickerInput = props => {
  const {
    label,
    hint,
    control,
    errors,
    options,
    ...controllerProps
  } = props;

  return (
    <Field className="form-field">
      <div style={{display: 'flex', flexDirection: 'column', color: elRed}}>
        <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
        <Controller
          {...controllerProps}
          control={control}
          render={({ field }) => (
            <StyledPicker
              {...field}
              style={{marginLeft: '5px', marginTop: '-6px'}}
            >
              <option
                key='picker-default'
                value=""
                label='Select'
              />
              {options?.map(option => (
                <option
                  key={option.value}
                  value={option.value}
                  label={option.label}
                >
                  {option.label}
                </option>
              ))}
            </StyledPicker>
          )}
        />
      </div>
    </Field>
  );
};

const SearchableSelectControl = ({ field, options }) => {
  const [focused, setFocused] = useState(false);
  const withAny = [{ value: '', label: 'Any' }, ...(options || [])];
  const selected =
    withAny.find(option => String(option.value) === String(field.value)) ||
    (field.value ? { value: field.value, label: String(field.value) } : null);

  return (
    <Select
      name={field.name}
      onFocus={() => setFocused(true)}
      onBlur={() => { setFocused(false); field.onBlur(); }}
      options={withAny}
      value={focused && !field.value ? null : selected}
      onChange={option => field.onChange(option ? option.value : '')}
      placeholder="Type to search"
      styles={searchablePickerStyles}
    />
  );
};

export const SearchablePickerInput = props => {
  const {
    label,
    hint,
    control,
    errors,
    options,
    ...controllerProps
  } = props;

  return (
    <Field className="form-field">
      <div style={{display: 'flex', flexDirection: 'column', color: elRed}}>
        <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
        <Controller
          {...controllerProps}
          control={control}
          render={({ field }) => (
            <SearchableSelectControl field={field} options={options} />
          )}
        />
      </div>
    </Field>
  );
};

export const StatePickerInput = props => {
  const {
    label,
    hint,
    control,
    errors,
    options,
    ...controllerProps
  } = props;

  return (
    <Field className="form-field">
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <StyledPicker
            {...field}
          >
            <option
              value=""
              label="Select"
            >
              {'Select'}
            </option>
            {abbreviations.map(option => (
              <option
                key={option}
                value={option}
                label={option}
              >
                {option}
              </option>
            ))}
          </StyledPicker>
        )}
      />
    </Field>
  );
};

export const SwitchInput = props => {
  const {
    label,
    control,
    ...controllerProps
  } = props;

  return (
    <SwitchField>
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <Switch
            {...field}
            activeTrackColor={colors.lightButton}
            activeThumbColor={colors.button}
            onValueChange={field.onChange}
          />
        )}
      />
      <SwitchLabel>{label}</SwitchLabel>
    </SwitchField>
  );
};

export const FormattedPhoneInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    placeholder,
    secureTextEntry,
    ...controllerProps
  } = props;

  return (
    <Field className="form-field">
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <input
            {...field}
            ref={ref}
            secureTextEntry={secureTextEntry}
            placeholder={placeholder}
            onChangeText={field.onChange}
          />
        )}
      />
    </Field>
    // const standardizedPhone = value => {
    //   if (value) {
    //     return '+1' + value.replace(/[-()/\s+/]/g, '');
    //   }

    //   return null;
    // };
  )
})

export const YearPickerInput = props => {
  const {
    label,
    hint,
    control,
    errors,
    options,
    watchMin,
    watchMax,
    ...controllerProps
  } = props;

  let currentYear = moment().format('YYYY');
  let years = [];
  [...Array(100).keys()].forEach(n => {
    if(!(watchMin && currentYear - n < watchMin) && !(watchMax && currentYear - n > watchMax)) {
      years.push(currentYear - n);
    }
  })

  const yearOptions = years.map(year => ({ value: year, label: String(year) }));

  return (
    <Field className="form-field">
      <div style={{display: 'flex', flexDirection: 'column', color: elRed}}>
        <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
        <Controller
          {...controllerProps}
          control={control}
          render={({ field }) => (
            <SearchableSelectControl field={field} options={yearOptions} />
          )}
        />
      </div>
    </Field>
  );
};

export const CurrencyInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    placeholder,
    secureTextEntry,
    ...controllerProps
  } = props;

  return (
    <Field className="form-field">
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <div style={{marginTop: -6}}>
            <Text>$</Text>
            <StyledTextInput
              {...field}
              ref={ref}
              type='number'
              secureTextEntry={secureTextEntry}
              placeholder={placeholder}
            />
          </div>
        )}
      />
    </Field>
  );
});

export const NumberInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    placeholder,
    secureTextEntry,
    ...controllerProps
  } = props;

  return (
    <Field className="form-field">
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <StyledTextInput
            {...field}
            ref={ref}
            type='number'
            secureTextEntry={secureTextEntry}
            placeholder={placeholder}
            onChangeText={field.onChange}
            style={{width: '100%', height: '31px', boxSizing: 'border-box', fontSize: '14px', marginLeft: '5px', marginTop: '-6px'}}
          />
        )}
      />
    </Field>
  );
});

const createGroup = (groupName, options, setLocalValue) => {
  return (
    {
      label: (() => {
        return (
          <div
            style={{fontWeight: 'bold', textAlign: 'center', fontSize: '16px', cursor: 'pointer'}}
            onClick={() =>
              setLocalValue(value =>
                value.concat(options.filter(grpOpt => !value.includes(grpOpt)))
              )
            }
          >
            {`---${groupName}---`}
          </div>
        )
      })(),
      options: options
    }
  );
};

export const StateMultiSelectInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    placeholder,
    secureTextEntry,
    value,
    setValue,
    defaultValues,
    ...controllerProps
  } = props;

  const [localValue, setLocalValue] = useState([]);

  useEffect(() => {

    if(localValue.length > 0) {
      setValue('state', localValue.map(values => values.value))
    }
  }, [localValue])

  const mapped = Object.keys(statesList).map(key => {
    return {
      label: statesList[key]['label'],
      value: key,
      region: statesList[key]['region']
    }
  })

  useEffect(() => {
      let defaultSelected = []
      if(defaultValues?.length) {
        defaultSelected = defaultValues.map(key => {
          return {
            label: statesList[key]['label'],
            value: key,
            region: statesList[key]['region']
          }
        })
      }
    setLocalValue(defaultSelected);
  }, [])


  const groupedOptions = mapped.reduce((groups, state) => {
    const region = state.region;
    if (!groups[region]) {
      groups[region] = [];
    }
    groups[region].push(state);
    return groups;
  }, {});

  const keys = Object.keys(groupedOptions);

  let options = [
    createGroup(keys[0], groupedOptions[keys[0]], setLocalValue),
    createGroup(keys[1], groupedOptions[keys[1]], setLocalValue),
    createGroup(keys[2], groupedOptions[keys[2]], setLocalValue),
    createGroup(keys[3], groupedOptions[keys[3]], setLocalValue),
  ]

  return (
    <Field className="form-field">
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} style={labelStyle} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <Select
            placeholder="Any"
            options={options}
            value={localValue}
            onChange={value => {
              if(!value.length) setValue('state', [])
              return setLocalValue(value)
            }}
            isMulti
            closeMenuOnSelect={false}
            styles={{
              ...selectStyles,

              valueContainer: (provided, state) => ({
                ...provided,
                height: '31px',
                padding: '0 px',
                overflowX: 'unset',
                overflowY: 'unset'
              }),

              input: (provided, state) => ({
                ...provided,
                margin: '0px',
              }),
              indicatorsContainer: (provided, state) => ({
                ...provided,
                height: '31px',
                overflowX: 'auto',
                overflowY: 'auto'
              }),
              placeholder: provided => ({ ...provided, color: '#000' }),
              option: provided => ({ ...provided, cursor: 'pointer' })
            }}
          />
        )}
      />
    </Field>
  );
});

const Input = {
  Text: TextInput,
  TextArea: TextAreaInput,
  Picker: PickerInput,
  SearchablePicker: SearchablePickerInput,
  StatePicker: StatePickerInput,
  Switch: SwitchInput,
  Phone: FormattedPhoneInput,
  YearPicker: YearPickerInput,
  Checkbox: CheckboxInput,
  Currency: CurrencyInput,
  Number: NumberInput,
  StateMultiSelect: StateMultiSelectInput
};

export default Input;
